export const copy = {
  titles: { pick: 'New diet plan', plans: 'Diet plans', tpl: 'Choose a template', goals: 'Goals', targets: 'Meal targets', meals: 'Meals', preview: 'Preview' },
  assess: 'View assessment',
  draftBtn: 'Save as draft',
  subs: {
    pick: 'Who is this plan for?', tpl: 'Start from a GoodFlip plan', goals: 'Step 1 of 4',
    targets: 'Step 2 of 4',
    meals: 'Step 3 of 4 · tap an option to edit', preview: (first: string) => `Step 4 of 4 · as ${first} sees it`,
  },
  /** Mobile build flow skips meal-targets screen (3 steps: goals → meals → preview). */
  subsMobile: {
    goals: 'Step 1 of 3',
    meals: 'Step 2 of 3 · tap an option to edit',
    preview: (first: string) => `Step 3 of 3 · as ${first} sees it`,
  },
  pick: { search: 'Search your patients', yours: 'Your patients', matching: 'Matching patients', active: 'Plan active', none: 'No plan', empty: (q: string) => `No patient matches "${q}"` },
  plans: { heading: 'Diet plans', empty: 'No diet plan yet. Create the first one below.', new: 'New diet plan' },
  tpl: { suggested: 'Suggested', mine: 'Mine', blank: 'Start from a blank plan', perDay: 'kcal/day' },
  goals: {
    name: 'Plan name', starts: 'Starts', ends: 'Ends', badDates: 'End date must be after the start',
    overlap: (name: string, from: string, to: string) =>
      `These dates overlap “${name}” (${from} – ${to}). A plan is already assigned then.`,
    useFreeDates: (from: string, to: string) => `Use ${from} – ${to} instead`,
    kcal: 'Daily calories', split: 'Macro split', advanced: 'Advanced · meal split',
    next: 'Next: meal targets', continue: 'Continue to meals', presets: { template: 'Template', balanced: 'Balanced', highP: 'High protein', lowC: 'Low carb' },
  },
  targets: {
    title: 'Set a target for every meal',
    sub: 'Each meal starts from the average of its template options, scaled to the daily goal. Edit any number; edited ones turn blue.',
    modeTemplate: "From template's foods",
    modeStandard: 'Standard meal shares',
    dailyGoal: 'Daily goal',
    editGoals: 'Edit goals',
    colOn: 'On',
    colMeal: 'Meal',
    colStart: 'Start',
    colEnd: 'End',
    colShare: 'Share',
    colKcal: 'kcal',
    colProtein: 'Protein g',
    colCarbs: 'Carbs g',
    colFats: 'Fats g',
    colMix: 'P/C/F mix',
    sourceTemplate: 'From template',
    sourceStandard: 'Daily split',
    includeMeal: (name: string) => `Include ${name}`,
    totalMeals: 'Total of meals',
    next: 'Next: fill meals',
    sumOk: (n: number) => `${n} meal targets add up to the daily calories`,
    sumOff: (pct: number) => `Meal shares add up to ${pct}% — adjust to 100%`,
    shareNot100: (pct: number) => `Meal shares add up to ${pct}%. Make it 100% to continue.`,
    scaleSharesStandard: 'Scale shares to 100%',
    macroTotalsGap: (parts: string[]) =>
      `Meals add up to ${parts.join(', ')}. Adjust a meal to close the gap, or leave it.`,
  },
  meals: {
    allOk: 'Every option is within 20% of its target',
    off: (n: number) => `${n} option${n > 1 ? 's' : ''} off target by more than 20%`,
    skipped: 'Skipped in this plan',
    include: 'Include',
    noFoods: 'No foods yet · tap to add',
    noFoodsYet: 'No foods yet',
    notPart: 'Not part of this plan',
    noTarget: 'No target',
    next: 'Preview & assign',
    previewAs: 'Preview as patient',
    option: 'Option',
    vsDailyGoal: 'Meal targets vs daily goal',
    mealsLabel: 'Meals',
    mealTarget: 'Meal target',
    toleranceHint: 'Full marks within ±20% (at least 3 g for macros).',
    setFromMedian: 'Set from options (avg)',
    onTarget: 'On target',
    tooHigh: 'Too high',
    tooLow: 'Too low',
    amountOver: (n: string, unit: string) => `${n} ${unit} over`,
    amountUnder: (n: string, unit: string) => `${n} ${unit} under`,
    withinRange: 'Within range',
    tipsTitle: 'Tips for this option',
    tipsBanner:
      'Say what to do: a short headline, then one line on how. Leave out the benefits; KAIRA explains them when the patient asks.',
    removeOption: 'Remove this option',
    addFood: '+ Add food',
    addOption: '+ Add option',
    addTip: '+ Add tip',
    tipCharOver: (max: number) => `Over the ${max}-character limit.`,
    tipsOverLimitFooter: 'Shorten prep tips over the character limit to preview or assign.',
    optionOff: (n: number) => `${n} option${n > 1 ? 's' : ''} off target`,
    onTargetStatus: 'On target',
  },
  option: {
    target: 'Meal target', edited: 'Edited', edit: 'Edit', reset: 'Reset', save: 'Save', cancel: 'Cancel',
    fromOpts: "Set from this meal's options (average)", addFood: 'Add food', tips: 'Prep tips for the patient', addTip: '+ Add tip',
    noTips: 'No tips yet.', untitled: 'Untitled tip', delete: 'Delete', done: 'Done', removed: 'Option removed',
    emptyLabel: 'No foods in this option yet',
  },
  kaira: {
    title: 'Suggested fixes',
    intro: 'If you need help finding foods that fill this target, KAIRA AI can suggest a few.',
    get: 'Get suggestions', loading: 'Finding foods…', again: 'Suggest again',
    add: 'Foods that could fill what this option is missing:',
    rich: (f: string, m: string) => `Low in ${f}. Swap in foods richer in ${f} that suit ${m}:`,
    light: (f: string, m: string) => f ? `High in ${f}. Swap in foods lower in ${f} that suit ${m}:` : `Over its calories. Swap in lighter foods that suit ${m}:`,
    note: (m: string, plan: string) => `Suggestions only, based on ${m} and this ${plan} plan. Add a food yourself with + Add food.`,
    none: "KAIRA couldn't find foods that fit this target. Edit the foods above, or adjust the meal target.",
    failed: 'KAIRA is unavailable right now. Try again in a moment.',
  },
  search: { title: 'Add food', placeholder: 'Search foods', often: 'Often used at this meal', db: 'Food database', empty: (q: string) => `Nothing matches "${q}"` },
  split: { title: 'Meal split', sub: 'Share of daily calories per meal', total: 'Total', targets: 'Macro targets', reset: 'Reset', template: 'Template', standard: 'Standard',
    sumOk: "Grams add up to this meal's calories", sumOff: (e: string, k: string) => `Grams add up to ${e} kcal — ratio is kept, calories stay ${k}` },
  preview: { hi: (f: string) => `Hi ${f}, your new plan`, by: 'by', pickAny: (n: number) => `Pick any 1 of ${n}`, one: '1 option', emptyOpt: 'Empty option',
    how: (f: string) => `This is how ${f} sees it in the GoodFlip app — every option per meal, with your prep tips. Dots show each option's target status.`,
    saveMaster: 'Also update the master template', saveMasterNote: (t: string) => `Changes apply to "${t}" for every coach`,
    saveMasterChanges: 'Update the master template',
    saveMasterChangesNote: (t: string) => `Apply these changes to "${t}" for every coach.`,
    saveTplSection: 'Save as a template',
    saveTplNone: "Don't save to templates",
    saveTplCopy: 'Save as a new copy template',
    saveTplCopyNote: 'Creates a new template in your list. The master template stays unchanged.',
    saveTplCopyName: 'New template name',
    saveTplCopyPh: 'e.g. Weight loss, 1,200 kcal (my copy)',
    saveMine: 'Also save as my template', saveMineNote: 'Reuse this plan for other patients', assign: (f: string) => `Assign to ${f}`,
    sheetTitle: 'Preview and assign', backToEdit: 'Back to editing', farFrom: (n: number) => `${n} option${n === 1 ? '' : 's'} ${n === 1 ? 'is' : 'are'} far from the meal target`,
    optionNums: (nums: number[]) => (nums.length === 1 ? `Option ${nums[0]}` : `Options ${nums.join(', ')}`) },
  assign: {
    title: 'Assign plan', sub: (f: string) => `Goes live in ${f}'s app right away`, tplName: 'Template name',
    fix: 'Fix these before assigning', off: (n: number) => `${n} option${n > 1 ? 's are' : ' is'} off target`,
    emptyNote: (f: string) => `${f} would see an empty option as a blank card.`, splitNote: 'Open Advanced · meal split on Goals to fix it.',
    offNote: 'A patient eating these options as planned will score lower on the day.',
    confirm: "I've reviewed these and want to assign anyway", why: 'Why are you assigning it anyway?',
    whyPh: 'For example, a veg option for days the patient does not eat eggs', needed: 'Needed before you can assign.',
    wa: 'Share on WhatsApp', waNote: (f: string) => `Sends ${f} a summary and a link to the plan`,
    button: 'Assign plan', anyway: 'Assign anyway', blocked: 'Fix the issues first', needReason: 'Confirm and add a reason first',
    splitIssue: (s: number) => `adds up to ${s}%, not 100%`, mealIssue: (bad: number, all: number, empty: number) => `${bad} of ${all} option${all > 1 ? 's' : ''} off target${empty ? ` (${empty} empty)` : ''}`,
    done: (f: string) => `Plan assigned to ${f}`, doneWa: 'Plan assigned · WhatsApp sent',
    tplSavedMaster: 'Master template updated',
    tplSavedCopy: (n: string) => `Template "${n}" created`,
    tplSaveFailed: 'Plan assigned, but the template could not be saved.',
  },
  leave: { title: 'Discard changes?', body: "You've made changes to this plan. Going back to templates will discard them.", discard: 'Discard changes', keep: 'Keep editing' },
  draft: { saving: 'Saving…', saved: 'Draft saved', failed: 'Draft not saved — retrying' },
};
